import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * CookieConsent - Minimal GDPR-compliant cookie banner
 */
function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      try {
        const saved = JSON.parse(consent);
        if (saved?.essential !== undefined) {
          setPreferences(saved);
          if (saved.analytics) initAnalytics();
        } else {
          localStorage.removeItem('cookieConsent');
          setTimeout(() => setShowBanner(true), 1000);
        }
      } catch {
        localStorage.removeItem('cookieConsent');
        setTimeout(() => setShowBanner(true), 1000);
      }
    }
  }, []);

  const initAnalytics = () => {
    //'Analytics enabled');
  };

  const saveAndClose = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    if (prefs.analytics) initAnalytics();
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  // Settings Modal
  if (showSettings) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b">
            <span className="font-semibold text-gray-900">Cookie Settings</span>
            <button onClick={() => setShowSettings(false)} className="p-1 hover:bg-gray-100 rounded">
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Essential */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Essential</p>
                <p className="text-xs text-gray-500">Required for site to work</p>
              </div>
              <div className="w-10 h-5 bg-green-500 rounded-full flex items-center justify-end px-0.5">
                <div className="w-4 h-4 bg-white rounded-full" />
              </div>
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Analytics</p>
                <p className="text-xs text-gray-500">Help us improve</p>
              </div>
              <button
                onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                className={`w-10 h-5 rounded-full transition-colors ${preferences.analytics ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <div className={`w-4 h-4 bg-white rounded-full transition-transform mx-0.5 ${preferences.analytics ? 'translate-x-5' : ''}`} />
              </button>
            </div>
          </div>

          <div className="flex gap-2 p-4 border-t bg-gray-50">
            <button
              onClick={() => saveAndClose({ essential: true, analytics: false })}
              className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Essential Only
            </button>
            <button
              onClick={() => saveAndClose(preferences)}
              className="flex-1 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main Banner - Compact
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
      <div className="max-w-6xl mx-auto px-4 py-3">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-gray-600 text-center sm:text-left">
            We use cookies for essential features and analytics.{' '}
            <a href="/privacy" className="text-blue-600 hover:underline">Learn more</a>
          </p>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowSettings(true)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
            >
              Settings
            </button>
            <button
              onClick={() => saveAndClose({ essential: true, analytics: false })}
              className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            >
              Decline
            </button>
            <button
              onClick={() => saveAndClose({ essential: true, analytics: true })}
              className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;